import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { WaterTowersService } from '../../../service/api/water-towers-service';
import { redux } from 'Core/redux';

export const STORE_KEY = 'waterTowers';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  maintenanceRecordsData: [],
  maintenanceRecordData: null,
  maintenanceData: null,
  cellHistoryData: [],
  tileView: true,
};

// Fetch all water towers
export const fetchWaterTowers = createAsyncThunk(
  'waterTowers/fetchWaterTowers',
  async () => {
    const { response } = await WaterTowersService.getAllWaterTowers();
    return response;
  }
);

// Fetch water towers with filters
export const fetchWaterTowersWithFilters = createAsyncThunk(
  'waterTowers/fetchWaterTowersWithFilters',
  async (filters = {}) => {
    const { response } = await WaterTowersService.getAllWaterTowers(filters);
    return response;
  }
);

// Save water towers
export const saveWaterTowers = createAsyncThunk(
  'waterTowers/saveWaterTowers',
  async (data) => {
    const { response } = await WaterTowersService.saveWaterTowers(data);
    return response;
  }
);

// Update water towers
export const updateWaterTowers = createAsyncThunk(
  'waterTowers/updateWaterTowers',
  async (data) => {
    const { response } = await WaterTowersService.updateWaterTowers(data);
    return response;
  }
);

// Add water towers (alias for save)
export const addWaterTowers = createAsyncThunk(
  'waterTowers/addWaterTowers',
  async (data) => {
    const { response } = await WaterTowersService.saveNewWaterTowers(data);
    return response;
  }
);

// Edit water towers (alias for update)
export const editWaterTowers = createAsyncThunk(
  'waterTowers/editWaterTowers',
  async (data) => {
    const { response } = await WaterTowersService.updateWaterTowers(data);
    return response;
  }
);

// Delete water towers
export const deleteWaterTowers = createAsyncThunk(
  'waterTowers/deleteWaterTowers',
  async (id) => {
    const { response } = await WaterTowersService.deleteWaterTowers(id);
    return response;
  }
);

// Save water towers maintenance
export const saveWaterTowersMaintenance = createAsyncThunk(
  'waterTowers/saveWaterTowersMaintenance',
  async (data) => {
    const { response } = await WaterTowersService.saveWaterTowersMaintenance(data);
    return response;
  }
);

// Fetch maintenance records by water towers ID
export const fetchMaintenanceRecordsByWaterTowersId = createAsyncThunk(
  'waterTowers/fetchMaintenanceRecordsByWaterTowersId',
  async (waterTowerId) => {
    const { response } = await WaterTowersService.getMaintenanceRecordsByWaterTowerId(waterTowerId);
    return response;
  }
);

// Fetch maintenance record details
export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'waterTowers/fetchMaintenanceRecordsDetails',
  async (recordId) => {
    const { response } = await WaterTowersService.getMaintenanceRecordDetails(recordId);
    return response;
  }
);

// Fetch water tower by asset number for cell history
export const fetchWaterTowerByAssetNumber = createAsyncThunk(
  'waterTowers/fetchByAssetNumber',
  async (assetNumber) => {
    const { response} = await WaterTowersService.getAllWaterTowerByAssetNumber(assetNumber);
    return response;
  }
);

// Add water towers maintenance record
export const addWaterTowersMaintenanceRecord = createAsyncThunk(
  'waterTowers/addWaterTowersMaintenanceRecord',
  async (data) => {
    const { response } = await WaterTowersService.addMaintenanceRecord(data);
    return response;
  }
);

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setWaterTowers: (state, action) => {
      // Ensure data is always an array
      if (Array.isArray(action.payload)) {
        state.data = action.payload;
      } else if (action.payload && typeof action.payload === 'object') {
        // If payload is a single object, wrap it in an array
        state.data = [action.payload];
      } else {
        state.data = [];
      }
    },
    setWaterTower: (state, action) => {
      // Ensure data is always an array
      if (Array.isArray(action.payload)) {
        state.data = action.payload;
      } else if (action.payload && typeof action.payload === 'object') {
        // If payload is a single object, wrap it in an array
        state.data = [action.payload];
      } else {
        state.data = [];
      }
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
    resetFilters: (state) => {
      // Reset any filter-related state if needed
      state.loading = false;
      state.error = null;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch water towers
      .addCase(fetchWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch water towers with filters
      .addCase(fetchWaterTowersWithFilters.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterTowersWithFilters.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchWaterTowersWithFilters.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save water towers
      .addCase(saveWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(saveWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Update water towers
      .addCase(updateWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(updateWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add water towers
      .addCase(addWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = [...state.data, action.payload];
      })
      .addCase(addWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Edit water towers
      .addCase(editWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Delete water towers
      .addCase(deleteWaterTowers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWaterTowers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = state.data.filter(item => item.id !== action.payload);
      })
      .addCase(deleteWaterTowers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Save water towers maintenance
      .addCase(saveWaterTowersMaintenance.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWaterTowersMaintenance.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceData = action.payload;
      })
      .addCase(saveWaterTowersMaintenance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance records by water towers ID
      .addCase(fetchMaintenanceRecordsByWaterTowersId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByWaterTowersId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByWaterTowersId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch maintenance record details
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordData = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Add water towers maintenance record
      .addCase(addWaterTowersMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addWaterTowersMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecordsData = [...state.maintenanceRecordsData, action.payload];
      })
      .addCase(addWaterTowersMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch water tower by asset number (cell history)
      .addCase(fetchWaterTowerByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWaterTowerByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchWaterTowerByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setWaterTowers, 
  setWaterTower, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;